/** Strings of the shell screens (login, layout, nav, client dashboard): `shellEn` is the source, `shellCs` must have the same keys. */
export const shellEn = {
  login: {
    tagline: "All your tools. One central hub.",
    description:
      "Every service your team relies on, connected and in sync — projects, time, billing, and beyond.",
    welcome: "Welcome back",
    signInWithGitlab: "Sign in with your GitLab account",
    continueWithGitlab: "Continue with GitLab",
    restricted: "Access is restricted to organization members",
    devLogin: "Dev login — no GitLab running (yarn start:gitlab for the real OAuth flow)",
    signIn: "Sign in",
    linkSent:
      "If {{email}} has a client account, a sign-in link is on its way. It works once, for 15 minutes.",
    clientWithoutGitlab: "Client without GitLab?",
    email: "E-mail",
    emailPlaceholder: "you@company.com",
    password: "Password",
    emailLink: "E-mail me a link",
    noPassword: "No password yet, or forgot it? Get a sign-in link and set one in your profile.",
    linkBroken: "This link doesn't work any more",
    linkBrokenHint: "A sign-in link works once and only for a limited time. Ask for a new one.",
    backToSignIn: "Back to sign-in",
    signingIn: "Signing you in…",
    errorDenied: "This account has no access to Hub.",
    errorOther: "Signing in with GitLab failed. Try again.",
  },
  dashboard: {
    title: "Dashboard",
    subtitle: "Your projects and their requests.",
    noProjects: "You aren't on any project yet. Your contact at Netlion will add you.",
    open: "open",
    done: "done",
    lastChange: "Last change",
    noRequests: "No requests yet",
    workItems: "Work items",
    wiki: "Wiki",
    status: {
      new: "new",
      spec_drafting: "spec drafting",
      spec_client_review: "spec client review",
      estimation: "estimation",
      approving: "approving",
      in_progress: "in progress",
      merge_to_dev: "merge to dev",
      internal_test_dev: "internal test dev",
      client_test_dev: "client test dev",
      merge_to_test: "merge to test",
      internal_test_test: "internal test test",
      client_test_test: "client test test",
      merge_to_main: "merge to main",
      internal_test_main: "internal test main",
      client_test_main: "client test main",
      client_review: "client review",
      done: "done",
      blocked: "blocked",
      waiting_on_client: "waiting on client",
      waiting_on_dependency: "waiting on dependency",
      cancelled: "cancelled",
      triage: "triage",
    },
  },
  appShell: {
    closeMenu: "Close menu",
    openMenu: "Open menu",
    viewingAs: "Viewing as {{name}}",
    viewAsReadOnly: "— read-only. You're {{name}}.",
    exiting: "Exiting…",
    exitViewAs: "Exit view-as",
    signedOut: "You are signed out",
    connectionLost: "Connection lost",
    layoutFailed: "Could not load layout",
    sessionExpired: "Your session expired.",
    signInLink: "Sign in",
    toContinue: "to continue.",
    networkDropped: "Your network connection dropped. Check your connection and try again.",
    retrying: "Retrying…",
    retry: "Retry",
  },
  routeError: {
    updated: "Hub was updated",
    reconnecting: "Reconnecting…",
    pageFailed: "This page failed to load",
    updatedHint:
      "A new version went out and this page needs a reload. Text you were typing is kept.",
    reconnectingHint:
      "Hub isn't answering right now (an update may be going out). This page comes back by itself.",
    reload: "Reload",
    retrying: "Retrying…",
    retry: "Retry",
  },
  connection: {
    restored: "Connection restored",
    lost: "Connection lost",
    reconnecting: "Reconnecting…",
  },
  palette: {
    placeholder: "Search issues, projects, customers...",
    esc: "Esc",
    issues: "Issues",
    projects: "Projects",
    customers: "Customers",
    users: "Users",
    noResults: "No results for “{{query}}”",
    typeMore: "Type at least 2 characters to search",
    navigate: "navigate",
    open: "open",
    close: "close",
  },
  appNav: {
    search: "Search...",
    dashboard: "Dashboard",
    workItems: "Work Items",
    wiki: "Wiki",
    noProjects: "No projects yet.",
    collapse: "Collapse {{label}}",
    agentWorking: "{{name}} is at work",
    expand: "Expand {{label}}",
  },
  projectTabs: {
    overview: "Overview",
    "work-items": "Work Items",
    releases: "Releases",
    "time-entries": "Time Entries",
    members: "Members",
    wiki: "Wiki",
    files: "Files",
    keys: "Keys",
    suggestions: "Suggestions",
    planner: "Planner",
    settings: "Edit",
  },
  projectHeader: {
    project: "Project",
  },
  relativeTime: {
    justNow: "just now",
    minutesAgo: "{{count}}m ago",
    hoursAgo: "{{count}}h ago",
    yesterday: "Yesterday",
    daysAgo_one: "{{count}} day ago",
    daysAgo_other: "{{count}} days ago",
    daysAgo_few: "{{count}} days ago",
  },
};

export const shellCs: typeof shellEn = {
  login: {
    tagline: "Všechny vaše nástroje. Jedno centrální místo.",
    description:
      "Všechny služby, na kterých váš tým stojí, propojené a synchronizované — projekty, čas, fakturace i další.",
    welcome: "Vítejte zpět",
    signInWithGitlab: "Přihlaste se účtem GitLab",
    continueWithGitlab: "Pokračovat přes GitLab",
    restricted: "Přístup mají jen členové organizace",
    devLogin: "Vývojové přihlášení — GitLab neběží (skutečné OAuth přihlášení: yarn start:gitlab)",
    signIn: "Přihlásit se",
    linkSent:
      "Pokud má adresa {{email}} klientský účet, odkaz pro přihlášení je na cestě. Platí jednou a 15 minut.",
    clientWithoutGitlab: "Klient bez GitLabu?",
    email: "E-mail",
    emailPlaceholder: "vy@firma.cz",
    password: "Heslo",
    emailLink: "Poslat odkaz e-mailem",
    noPassword:
      "Ještě nemáte heslo, nebo jste ho zapomněli? Pošleme vám odkaz a heslo si nastavíte v profilu.",
    linkBroken: "Tento odkaz už nefunguje",
    linkBrokenHint: "Přihlašovací odkaz platí jen jednou a omezenou dobu. Požádejte o nový.",
    backToSignIn: "Zpět na přihlášení",
    signingIn: "Přihlašuji…",
    errorDenied: "Tento účet nemá přístup do Hubu.",
    errorOther: "Přihlášení přes GitLab se nepovedlo. Zkuste to znovu.",
  },
  dashboard: {
    title: "Přehled",
    subtitle: "Vaše projekty a jejich požadavky.",
    noProjects: "Zatím nejste v žádném projektu. Váš kontakt v Netlionu vás přidá.",
    open: "otevřených",
    done: "hotových",
    lastChange: "Poslední změna",
    noRequests: "Zatím žádné požadavky",
    workItems: "Tikety",
    wiki: "Wiki",
    status: {
      new: "nový",
      spec_drafting: "píše se zadání",
      spec_client_review: "zadání u klienta",
      estimation: "odhad",
      approving: "schvalování",
      in_progress: "v řešení",
      merge_to_dev: "slučování do dev",
      internal_test_dev: "interní test dev",
      client_test_dev: "test klienta dev",
      merge_to_test: "slučování do test",
      internal_test_test: "interní test test",
      client_test_test: "test klienta test",
      merge_to_main: "slučování do main",
      internal_test_main: "interní test main",
      client_test_main: "test klienta main",
      client_review: "ke kontrole klientem",
      done: "hotovo",
      blocked: "blokováno",
      waiting_on_client: "čeká na klienta",
      waiting_on_dependency: "čeká na závislost",
      cancelled: "zrušeno",
      triage: "k roztřídění",
    },
  },
  appShell: {
    closeMenu: "Zavřít menu",
    openMenu: "Otevřít menu",
    viewingAs: "Zobrazeno jako {{name}}",
    viewAsReadOnly: "— jen pro čtení. Jste {{name}}.",
    exiting: "Ukončuji…",
    exitViewAs: "Ukončit zobrazení jako",
    signedOut: "Jste odhlášeni",
    connectionLost: "Spojení se ztratilo",
    layoutFailed: "Nepodařilo se načíst rozvržení",
    sessionExpired: "Platnost vaší relace vypršela.",
    signInLink: "Přihlaste se",
    toContinue: "a pokračujte.",
    networkDropped: "Síťové připojení se přerušilo. Zkontrolujte připojení a zkuste to znovu.",
    retrying: "Zkouším znovu…",
    retry: "Zkusit znovu",
  },
  routeError: {
    updated: "Hub byl aktualizován",
    reconnecting: "Připojuji se znovu…",
    pageFailed: "Tuto stránku se nepodařilo načíst",
    updatedHint:
      "Vyšla nová verze a stránku je potřeba znovu načíst. Text, který jste psali, zůstane zachován.",
    reconnectingHint:
      "Hub teď neodpovídá (možná právě probíhá aktualizace). Stránka se obnoví sama.",
    reload: "Načíst znovu",
    retrying: "Zkouším znovu…",
    retry: "Zkusit znovu",
  },
  connection: {
    restored: "Spojení obnoveno",
    lost: "Spojení se ztratilo",
    reconnecting: "Připojuji se znovu…",
  },
  palette: {
    placeholder: "Hledat tikety, projekty, zákazníky...",
    esc: "Esc",
    issues: "Tikety",
    projects: "Projekty",
    customers: "Zákazníci",
    users: "Uživatelé",
    noResults: "Nic nenalezeno pro „{{query}}“",
    typeMore: "Pro hledání zadejte aspoň 2 znaky",
    navigate: "procházet",
    open: "otevřít",
    close: "zavřít",
  },
  appNav: {
    search: "Hledat...",
    dashboard: "Přehled",
    workItems: "Tikety",
    wiki: "Wiki",
    noProjects: "Zatím žádné projekty.",
    collapse: "Sbalit {{label}}",
    agentWorking: "{{name}} právě pracuje",
    expand: "Rozbalit {{label}}",
  },
  projectTabs: {
    overview: "Přehled",
    "work-items": "Tikety",
    releases: "Vydání",
    "time-entries": "Výkazy času",
    members: "Členové",
    wiki: "Wiki",
    files: "Soubory",
    keys: "Klíče",
    suggestions: "Návrhy",
    planner: "Plánovač",
    settings: "Upravit",
  },
  projectHeader: {
    project: "Projekt",
  },
  relativeTime: {
    justNow: "právě teď",
    minutesAgo: "před {{count}} min",
    hoursAgo: "před {{count}} h",
    yesterday: "Včera",
    daysAgo_one: "před {{count}} d",
    daysAgo_other: "před {{count}} d",
    daysAgo_few: "před {{count}} d",
  },
};
